/**
 * Enterprise Automation Module Component
 * Security Compliance Automation Rules Engine
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { automationService } from '../../services/enterpriseService';
import { Modal } from './SharedModals';
import {
  Zap, Play, Pause, Plus, Settings, Clock, CheckCircle, XCircle,
  AlertTriangle, RefreshCw, ChevronRight, Filter, Search, Code,
  GitBranch, Activity, History, BarChart2, Eye, Trash2, Edit
} from 'lucide-react';

interface AutomationRule {
  id: number;
  name: string;
  description?: string;
  trigger_type: 'schedule' | 'event' | 'manual' | 'api_call';
  trigger_config: Record<string, any>;
  condition_logic?: string;
  action_type: string;
  action_config: Record<string, any>;
  is_active: boolean;
  priority: number;
  last_executed_at?: string;
  execution_count: number;
  created_at: string;
}

interface ExecutionStats {
  total_executions: number;
  successful: number;
  failed: number;
  avg_duration_ms: number;
  rules_by_status: Record<string, number>;
}

export const EnterpriseAutomation: React.FC = () => {
  const { language } = useI18n();
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [stats, setStats] = useState<ExecutionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'rules' | 'executions' | 'analytics'>('rules');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showNewRuleModal, setShowNewRuleModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_type: 'event' as 'schedule' | 'event' | 'manual' | 'api_call',
    action_type: 'notify',
    is_active: true,
    priority: 5
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesData, statsData] = await Promise.all([
        automationService.getRules(),
        automationService.getExecutionStats().catch(() => null)
      ]);
      
      // Handle API response format - may be { data: [...] }, { rules: [...] }, or direct array
      const rulesList = rulesData?.data || rulesData?.rules || (Array.isArray(rulesData) ? rulesData : []);
      setRules(rulesList);
      setStats(statsData?.data || statsData);
    } catch (error) {
      console.error('Error loading automation data:', error);
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (ruleId: number, isActive: boolean) => {
    try {
      await automationService.updateRule(ruleId, { is_active: isActive });
      setRules(prev => prev.map(r => r.id === ruleId ? { ...r, is_active: isActive } : r));
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name.trim()) return;
    setSaving(true);
    try {
      await automationService.createRule({
        name: newRule.name,
        description: newRule.description,
        trigger_type: newRule.trigger_type,
        trigger_config: {},
        action_type: newRule.action_type,
        action_config: {},
        is_active: newRule.is_active,
        priority: newRule.priority
      });
      setShowNewRuleModal(false);
      setNewRule({ name: '', description: '', trigger_type: 'event', action_type: 'notify', is_active: true, priority: 5 });
      loadData();
    } catch (error) {
      console.error('Error creating rule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleManualTrigger = async (ruleId: number) => {
    try {
      await automationService.fireTrigger('manual', { rule_id: ruleId });
      loadData();
    } catch (error) {
      console.error('Error triggering rule:', error);
    }
  };

  const triggerTypes = {
    schedule: { label: language === 'sr' ? 'Raspored' : 'Schedule', icon: Clock, color: 'blue' },
    event: { label: language === 'sr' ? 'Događaj' : 'Event', icon: Zap, color: 'purple' },
    manual: { label: language === 'sr' ? 'Ručno' : 'Manual', icon: Play, color: 'green' },
    api_call: { label: 'API', icon: Code, color: 'orange' }
  };

  const getTriggerColor = (type: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
    };
    const typeInfo = triggerTypes[type as keyof typeof triggerTypes];
    return colors[typeInfo?.color || 'blue'];
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || rule.trigger_type === filterType;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Zap className="w-7 h-7 text-amber-500" />
            {language === 'sr' ? 'Automatizacija usklađenosti' : 'Compliance Automation'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Upravljajte automatizovanim pravilima i procesima'
              : 'Manage automated rules and compliance workflows'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowNewRuleModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {language === 'sr' ? 'Novo pravilo' : 'New Rule'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <GitBranch className="w-8 h-8 text-indigo-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{rules.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Ukupno pravila' : 'Total Rules'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {rules.filter(r => r.is_active).length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Aktivna pravila' : 'Active Rules'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.total_executions || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Ukupno izvršenja' : 'Total Executions'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.failed || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Neuspjela izvršenja' : 'Failed Executions'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'rules', icon: GitBranch, label: language === 'sr' ? 'Pravila' : 'Rules' },
          { key: 'executions', icon: History, label: language === 'sr' ? 'Izvršenja' : 'Executions' },
          { key: 'analytics', icon: BarChart2, label: language === 'sr' ? 'Analitika' : 'Analytics' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži pravila...' : 'Search rules...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="all">{language === 'sr' ? 'Svi tipovi' : 'All Types'}</option>
              <option value="schedule">{language === 'sr' ? 'Raspored' : 'Schedule'}</option>
              <option value="event">{language === 'sr' ? 'Događaj' : 'Event'}</option>
              <option value="manual">{language === 'sr' ? 'Ručno' : 'Manual'}</option>
              <option value="api_call">API</option>
            </select>
          </div>

          {/* Rules List */}
          <div className="space-y-3">
            {filteredRules.map(rule => {
              const TriggerIcon = triggerTypes[rule.trigger_type as keyof typeof triggerTypes]?.icon || Zap;
              
              return (
                <div key={rule.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        rule.is_active ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <TriggerIcon className={`w-6 h-6 ${rule.is_active ? 'text-amber-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{rule.name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs ${getTriggerColor(rule.trigger_type)}`}>
                            {triggerTypes[rule.trigger_type as keyof typeof triggerTypes]?.label}
                          </span>
                          {rule.is_active ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs">
                              {language === 'sr' ? 'Aktivno' : 'Active'}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 rounded text-xs">
                              {language === 'sr' ? 'Neaktivno' : 'Inactive'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{rule.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {rule.execution_count} {language === 'sr' ? 'izvršenja' : 'executions'}
                          </span>
                          {rule.last_executed_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(rule.last_executed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {rule.trigger_type === 'manual' && (
                        <button 
                          onClick={() => handleManualTrigger(rule.id)}
                          className="p-2 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg"
                          title={language === 'sr' ? 'Pokreni' : 'Run'}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleToggleRule(rule.id, !rule.is_active)}
                        className={`p-2 rounded-lg ${
                          rule.is_active 
                            ? 'text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30' 
                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={rule.is_active ? (language === 'sr' ? 'Pauziraj' : 'Pause') : (language === 'sr' ? 'Aktiviraj' : 'Activate')}
                      >
                        {rule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredRules.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'sr' ? 'Nema pravila automatizacije' : 'No automation rules found'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Executions Tab */}
      {activeTab === 'executions' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            {language === 'sr' ? 'Historija izvršenja - dolazi uskoro' : 'Execution history - coming soon'}
          </p>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            {language === 'sr' ? 'Analitika automatizacije - dolazi uskoro' : 'Automation analytics - coming soon'}
          </p>
        </div>
      )}

      {/* New Rule Modal */}
      <Modal
        isOpen={showNewRuleModal}
        onClose={() => setShowNewRuleModal(false)}
        title={language === 'sr' ? 'Novo pravilo automatizacije' : 'New Automation Rule'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowNewRuleModal(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleCreateRule}
              disabled={saving || !newRule.name.trim()}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Kreiranje...' : 'Creating...') : (language === 'sr' ? 'Kreiraj pravilo' : 'Create Rule')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv pravila' : 'Rule Name'} *
            </label>
            <input
              type="text"
              value={newRule.name}
              onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
              placeholder={language === 'sr' ? 'npr. Eskalacija kritičnog rizika' : 'e.g. Critical Risk Escalation'}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Opis' : 'Description'}
            </label>
            <textarea
              value={newRule.description}
              onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={language === 'sr' ? 'Opišite šta ovo pravilo radi...' : 'Describe what this rule does...'}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Tip okidača' : 'Trigger Type'}
              </label>
              <select
                value={newRule.trigger_type}
                onChange={(e) => setNewRule(prev => ({ ...prev, trigger_type: e.target.value as any }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="event">{language === 'sr' ? 'Događaj' : 'Event-driven'}</option>
                <option value="schedule">{language === 'sr' ? 'Raspored' : 'Scheduled'}</option>
                <option value="manual">{language === 'sr' ? 'Ručno' : 'Manual'}</option>
                <option value="api_call">API Webhook</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Tip akcije' : 'Action Type'}
              </label>
              <select
                value={newRule.action_type}
                onChange={(e) => setNewRule(prev => ({ ...prev, action_type: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="notify">{language === 'sr' ? 'Obavijesti' : 'Send Notification'}</option>
                <option value="update_status">{language === 'sr' ? 'Ažuriraj status' : 'Update Status'}</option>
                <option value="create_task">{language === 'sr' ? 'Kreiraj zadatak' : 'Create Task'}</option>
                <option value="escalate">{language === 'sr' ? 'Eskaliraj' : 'Escalate'}</option>
                <option value="generate_report">{language === 'sr' ? 'Generiši izvještaj' : 'Generate Report'}</option>
                <option value="run_scan">{language === 'sr' ? 'Pokreni skeniranje' : 'Run Scan'}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Prioritet (1-10)' : 'Priority (1-10)'}
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={newRule.priority}
                onChange={(e) => setNewRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 5 }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newRule.is_active}
                  onChange={(e) => setNewRule(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 text-amber-500 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {language === 'sr' ? 'Aktiviraj odmah' : 'Activate immediately'}
                </span>
              </label>
            </div>
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {language === 'sr' 
                ? 'Dodatne konfiguracije okidača i uslova mogu se podesiti u postavkama pravila nakon kreiranja.'
                : 'Additional trigger and condition configurations can be set in rule settings after creation.'}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseAutomation;
